import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { useEffect } from 'react';
import { Drawer, showDrawer } from '@ardoq/drawer';
import {
  PrimaryButton,
  TertiaryButton,
  GhostButton,
  ButtonSize,
} from '@ardoq/button';
import { Box, FlexBox } from '@ardoq/layout';
import { Paragraph } from '@ardoq/typography';
import { InlineLink } from '@ardoq/link';
import { Tab, TabList } from '@ardoq/tabs';
import { Trans, t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import {
  CodeIcon,
  SpinnerIcon,
  CopyIcon,
  AnimatedChevron,
  IconSize,
} from '@ardoq/icons';
import { ErrorNotification } from '@ardoq/status-ui';
import styled from 'styled-components';
import { colors, spacing } from '@ardoq/design-tokens';
import { ImportAPIPayloadExamplesResponse } from '@ardoq/api-types/integrations';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { importApiPayloadDrawer$ } from './importApiPayloadDrawer$';
import { fetchImportApiPayloadExamples } from './actions';

import { routines } from './routines';
import { formattedJsonString, payloadToFormattedJsonString } from './utils';
import { ErrorDetails, TabType } from './types';
import { ASYNC_STATUS } from '../../types/api';

const ContentArea = styled(Box)`
  flex: 1;
  overflow: hidden;
`;

const CodeBlock = styled.pre`
  background-color: ${colors.bgSubtle};
  border: 1px solid ${colors.borderSubtle};
  border-radius: 0;
  padding: ${spacing.s16};
  margin: 0;
  overflow: auto;
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${colors.textDefault};
  height: 100%;
  width: 100%;
`;

const LoadingContainer = styled(FlexBox)`
  height: 200px;
`;

type ImportApiPayloadDrawerProps = {
  configId: string;
  asyncStatus: ASYNC_STATUS;
  payload: ImportAPIPayloadExamplesResponse | null;
  errorDetails: ErrorDetails | null;
  activeTab: TabType;
  copiedTab: TabType | null;
  showDetails: boolean;
  onChangeActiveTab: (newTab: TabType) => void;
  onClose: VoidFunction;
  onCopyToClipboard: VoidFunction;
  onToggleShowDetails: VoidFunction;
  i18nLocale: I18nLocale;
};

const ImportApiPayloadDrawerComponent = (
  props: ImportApiPayloadDrawerProps
) => {
  const {
    activeTab,
    asyncStatus,
    configId,
    copiedTab,
    errorDetails,
    onChangeActiveTab,
    onClose,
    onCopyToClipboard,
    onToggleShowDetails,
    payload,
    showDetails,
    i18nLocale,
  } = props;

  useEffect(() => {
    dispatchAction(fetchImportApiPayloadExamples(configId));
  }, [configId]);

  return (
    <Drawer
      drawerSize="medium"
      title={t(
        i18nLocale,
        'integrations:importApiPayloadDrawer.title',
        'Import API Payload Details'
      )}
      icon={<CodeIcon iconSize={IconSize.LARGE} />}
      subtitle={
        <Trans
          i18nKey="integrations:importApiPayloadDrawer.subtitle"
          defaults="Import API payload examples for this configuration. See <kbLink>Knowledge Base</kbLink> for more details."
          components={{
            kbLink: (
              // @ts-expect-error Trans supplies children from the translation
              <InlineLink
                href={KnowledgeBaseLink.IMPORT_API}
                target="_blank"
                opensInNewTabLabel={t(
                  i18nLocale,
                  'common:labels.opensInNewTab',
                  '(new tab)'
                )}
              />
            ),
          }}
        />
      }
      controls={
        <PrimaryButton onClick={onClose}>
          {t(i18nLocale, 'integrations:importApiPayloadDrawer.close', 'Close')}
        </PrimaryButton>
      }
    >
      {asyncStatus === ASYNC_STATUS.INIT && (
        <LoadingContainer justify="center" align="center">
          <SpinnerIcon />
        </LoadingContainer>
      )}

      {asyncStatus === ASYNC_STATUS.FAILURE && (
        <Box>
          <ErrorNotification>
            {errorDetails?.message}

            {errorDetails?.data && (
              <Box marginTop="medium">
                <GhostButton
                  buttonSize={ButtonSize.SMALL}
                  onClick={onToggleShowDetails}
                >
                  {showDetails
                    ? t(
                        i18nLocale,
                        'integrations:importApiPayloadDrawer.hideDetails',
                        'Hide details'
                      )
                    : t(
                        i18nLocale,
                        'integrations:importApiPayloadDrawer.showDetails',
                        'Show details'
                      )}
                  <AnimatedChevron $isUp={showDetails} />
                </GhostButton>

                {showDetails && errorDetails.data && (
                  <Box marginTop="small">
                    <CodeBlock>
                      {formattedJsonString(errorDetails.data)}
                    </CodeBlock>
                  </Box>
                )}
              </Box>
            )}
          </ErrorNotification>
        </Box>
      )}

      {asyncStatus === ASYNC_STATUS.SUCCESS && (
        <FlexBox height="full" flexDirection="column">
          <Box flexShrink={0} padding="xsmall">
            <TabList
              activeTabId={activeTab}
              onTabChange={newTab => onChangeActiveTab(newTab)}
              moreButtonLabel={t(i18nLocale, 'common:labels.more', 'More')}
            >
              <Tab
                label={t(
                  i18nLocale,
                  'integrations:importApiPayloadDrawer.objectsTab',
                  'Objects (Dictionaries)'
                )}
                tabId="objects"
              />
              <Tab
                label={t(
                  i18nLocale,
                  'integrations:importApiPayloadDrawer.arraysTab',
                  'Arrays (Tabular)'
                )}
                tabId="arrays"
              />
            </TabList>
          </Box>

          <FlexBox
            justify="space-between"
            align="center"
            padding="medium"
            flexShrink={0}
          >
            <Paragraph variant="text1">
              {t(
                i18nLocale,
                'integrations:importApiPayloadDrawer.payloadDescription',
                'Provide this payload to your Integrations team as an example when setting up your import:'
              )}
            </Paragraph>
            <TertiaryButton onClick={onCopyToClipboard}>
              <CopyIcon />
              {activeTab === copiedTab
                ? t(
                    i18nLocale,
                    'integrations:importApiPayloadDrawer.copied',
                    'Copied!'
                  )
                : t(
                    i18nLocale,
                    'integrations:importApiPayloadDrawer.copyToClipboard',
                    'Copy to clipboard'
                  )}
            </TertiaryButton>
          </FlexBox>

          {!!payload && (
            <ContentArea>
              <CodeBlock>
                {payloadToFormattedJsonString(activeTab, payload)}
              </CodeBlock>
            </ContentArea>
          )}
        </FlexBox>
      )}
    </Drawer>
  );
};

const ConnectedImportApiPayloadDrawer = connect(
  ImportApiPayloadDrawerComponent,
  importApiPayloadDrawer$,
  routines
);

export const showImportApiPayloadDrawer = (configId: string) => {
  // i18nLocale$.state OK here: imperative drawer opener invoked from event
  // handler.
  const i18nLocale = i18nLocale$.state;
  return showDrawer({
    stackPageName: `import-api-payload-drawer-${configId}`,
    shouldCloseOnEscapeKey: true,
    shouldCloseOnBackdropClick: true,
    renderDrawer: ({ handleCloserDrawer }) => (
      <ConnectedImportApiPayloadDrawer
        configId={configId}
        onClose={handleCloserDrawer}
        i18nLocale={i18nLocale}
      />
    ),
  });
};
